import { useEffect, useMemo, useState } from 'react';
import type { User, UserCompany } from '../types/user';
import { __ } from '@/hub-shared/utils/i18n';
import { getBootstrapData } from '@tpct/admin-ui';
import FeatureGateTooltip from '@/hub-shared/components/FeatureGateTooltip';
import {
  getAdvancedUserManagementGate,
  type FeatureGateDecision,
} from '@/hub-shared/gates/featureGateDecisions';
import { handleApiError, revokeUserCompanyAccess } from '../utils/api';
import { archiveIconSm } from '@tpct/icons';
import UserCompaniesAssignModal from './UserCompaniesAssignModal';

type UserCompaniesTableProps = {
  user: User;
  bootstrapKey?: string;
  canManageUsers?: boolean;
  canUseAdvancedUserManagement?: boolean;
  advancedUserManagementGate?: FeatureGateDecision;
  onUserRefresh?: (user: User) => void;
};

const UserCompaniesTable = ({
  user,
  bootstrapKey = 'AccountingHubUsersAdmin',
  canManageUsers = true,
  canUseAdvancedUserManagement = true,
  advancedUserManagementGate,
  onUserRefresh,
}: UserCompaniesTableProps) => {
  const [companies, setCompanies] = useState<UserCompany[]>(() => user.companies ?? []);
  const [busyCompanyId, setBusyCompanyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const viaCollaboratorsMode = user.company_scope_via_collaborators === true;
  const dossierAttributionManual = useMemo(() => {
    try {
      const bs = getBootstrapData(bootstrapKey);
      return bs.dossierAttributionDefaultScopeMode === 'explicit';
    } catch {
      return false;
    }
  }, [bootstrapKey]);
  const canAssignDossiers = dossierAttributionManual && !viaCollaboratorsMode;
  const gate = advancedUserManagementGate ?? getAdvancedUserManagementGate({
    advancedUserManagement: canUseAdvancedUserManagement,
  });

  useEffect(() => {
    setCompanies(user.companies ?? []);
    setBusyCompanyId(null);
    setError(null);
    setIsAssignModalOpen(false);
  }, [user]);

  const handleRevoke = async (companyId: number) => {
    if (!canUseAdvancedUserManagement) {
      return;
    }

    setBusyCompanyId(companyId);
    setError(null);

    try {
      await revokeUserCompanyAccess(user.id, companyId, bootstrapKey);
      setCompanies((prev) => prev.filter((company) => company.id !== companyId));
    } catch (err: unknown) {
      setError(handleApiError(err));
    } finally {
      setBusyCompanyId(null);
    }
  };

  const handleOpenAssignModal = () => {
    if (!canUseAdvancedUserManagement) {
      return;
    }

    setError(null);
    setIsAssignModalOpen(true);
  };

  const handleSaveAssignedCompanies = (nextCompanies: UserCompany[]) => {
    setCompanies(nextCompanies);
    setIsAssignModalOpen(false);
  };

  return (
    <div className="container-style user-companies-panel">
      {viaCollaboratorsMode ? (
        <p className="compliance-tab__hint">{__('Les dossiers de cet expert correspondent à la combinaison des collaborateurs reliés.')}</p>
      ) : null}
      <table className="thn-table user-companies-table">
        <thead>
          <tr>
            <th scope="col">{viaCollaboratorsMode ? __('Dossiers visibles') : __('Client')}</th>
            <th scope="col">{__('Actions')}</th>
          </tr>
        </thead>
        <tbody>
          {companies.length === 0 ? (
            <tr>
              <td colSpan={2}>
                {viaCollaboratorsMode
                  ? __('Aucun dossier résolu : ajoutez des collaborateurs reliés qui ont eux-mêmes des dossiers attribués.')
                  : __('Aucun dossier client rattaché')}
              </td>
            </tr>
          ) : (
            companies.map((company) => {
              const isBusy = busyCompanyId === company.id;

              return (
                <tr key={company.id}>
                  <td>{company.name}</td>
                  <td>
                    {!canManageUsers ? (
                      <span aria-hidden="true">—</span>
                    ) : viaCollaboratorsMode ? (
                      <span aria-hidden="true">—</span>
                    ) : (
                      <FeatureGateTooltip decision={gate}>
                        <button
                          type="button"
                          className="btn-tertiary user-companies-revoke-btn"
                          onClick={() => {
                            void handleRevoke(company.id);
                          }}
                          disabled={isBusy || !gate.allowed}
                        >
                          <img src={archiveIconSm} alt="" width={16} height={16} aria-hidden />
                          {isBusy ? __('Révocation…') : __('Révoquer l’accès')}
                        </button>
                      </FeatureGateTooltip>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      <div className="user-companies-panel-footer">
        {canManageUsers && (canAssignDossiers || viaCollaboratorsMode) ? (
          <FeatureGateTooltip decision={gate}>
            <button
              type="button"
              className="btn-primary add-to-list-btn"
              onClick={handleOpenAssignModal}
              disabled={!gate.allowed}
            >
              {viaCollaboratorsMode
                ? __('Gérer les collaborateurs reliés')
                : __('Attribuer un nouveau dossier')}
            </button>
          </FeatureGateTooltip>
        ) : null}
      </div>
      {error && <p className="notice notice-error">{error}</p>}
      {canManageUsers && (canAssignDossiers || viaCollaboratorsMode) ? (
        <UserCompaniesAssignModal
          open={isAssignModalOpen}
          userId={user.id}
          modalMode={viaCollaboratorsMode ? 'expert_via' : 'companies'}
          currentCompanies={companies}
          initialViaCollaboratorIds={user.via_collaborator_user_ids ?? []}
          bootstrapKey={bootstrapKey}
          onClose={() => setIsAssignModalOpen(false)}
          onSave={handleSaveAssignedCompanies}
          onUserRefresh={onUserRefresh}
        />
      ) : null}
    </div>
  );
};

export default UserCompaniesTable;
